<template>
  <v-container fluid class="transactions-page mt-10">
    <v-card class="transactions-page__filters" outlined>
      <v-card-text>
        <div class="transactions-page__toolbar">
          <div>
            <div class="text-h6">Transações</div>
            <div class="transactions-page__results">
              {{ totalTransactions }} resultado(s)
            </div>
          </div>

          <div class="transactions-page__toolbar-actions">
            <v-btn color="#e5183d" dark elevation="0" @click="filtersDialog = true">
              <v-icon left>mdi-filter-variant</v-icon>
              Filtros
            </v-btn>
            <v-btn
              v-if="activeFilterChips.length"
              outlined
              color="#e5183d"
              @click="clearFilters"
            >
              Limpar filtros
            </v-btn>
          </div>
        </div>

        <div v-if="activeFilterChips.length" class="transactions-page__chips">
          <span class="transactions-page__chips-label">Filtros ativos:</span>
          <v-chip
            v-for="chip in activeFilterChips"
            :key="chip.key"
            close
            small
            class="ma-1"
            @click:close="removeFilter(chip.key)"
          >
            {{ chip.label }}
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="filtersDialog"
      max-width="920"
      scrollable
      transition="dialog-bottom-transition"
      content-class="transactions-filters-dialog__backdrop"
    >
      <v-card class="transactions-filters-dialog" elevation="12">
        <div class="transactions-filters-dialog__accent" aria-hidden="true" />
        <v-card-title class="transactions-filters-dialog__header">
          <div class="transactions-filters-dialog__header-text">
            <div class="transactions-filters-dialog__title-row">
              <v-avatar size="40" color="#e5183d" class="transactions-filters-dialog__title-icon">
                <v-icon dark>mdi-filter-variant</v-icon>
              </v-avatar>
              <div>
                <div class="text-h6 transactions-filters-dialog__title mb-0">Filtros</div>
                <div class="transactions-filters-dialog__subtitle text-body-2">
                  Ajuste critérios e aplique para atualizar a lista.
                </div>
              </div>
            </div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Fechar" @click="filtersDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="transactions-filters-dialog__divider" />

        <v-card-text class="transactions-filters-dialog__body">
          <section class="transactions-filters-dialog__section">
            <h3 class="transactions-filters-dialog__section-label">Busca e identificação</h3>
            <p class="transactions-filters-dialog__section-hint">
              Busca geral cobre ID, cliente, parceiro aderente, associado responsável e demais campos.
            </p>
            <v-row dense>
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="filters.search"
                  outlined
                  dense
                  clearable
                  hide-details
                  append-icon="mdi-magnify"
                  label="Busca geral"
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="filters.transactionId"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="ID da transação"
                />
              </v-col>
            </v-row>
          </section>

          <section class="transactions-filters-dialog__section">
            <h3 class="transactions-filters-dialog__section-label">Classificação</h3>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Status"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="filters.type"
                  :items="typeOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Tipo de transação"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="filters.productCategory"
                  :items="productCategoryOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Produto / assinatura"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="filters.paymentMethod"
                  :items="paymentMethodOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Pagamento"
                />
              </v-col>
            </v-row>
          </section>

          <section class="transactions-filters-dialog__section">
            <h3 class="transactions-filters-dialog__section-label">Período e franquia</h3>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="periodPreset"
                  :items="periodPresetOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  label="Período"
                  @change="handlePeriodPresetChange"
                />
              </v-col>
              <v-col cols="12" sm="6" md="5">
                <v-dialog
                  ref="dateDialog"
                  v-model="dateDialog"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formattedDateRange"
                      label="Intervalo de datas"
                      prepend-inner-icon="mdi-calendar-range"
                      outlined
                      dense
                      readonly
                      hide-details
                      class="transactions-filters-dialog__date-field"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="selectedRange" range locale="pt-br">
                    <v-spacer />
                    <v-btn text @click="closeDateDialog">Cancelar</v-btn>
                    <v-btn color="primary" @click="confirmDateRange">Ok</v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.ibge"
                  :items="franchises"
                  item-text="cityName"
                  item-value="ibge"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Franquia"
                />
              </v-col>
            </v-row>
          </section>

          <section class="transactions-filters-dialog__section transactions-filters-dialog__section--last">
            <h3 class="transactions-filters-dialog__section-label">Parceiros e valores</h3>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="filters.partnerName"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Empresa / parceiro"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="filters.associateName"
                  outlined
                  dense
                  clearable
                  hide-details
                  label="Associado responsável"
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="filters.minValueInput"
                  outlined
                  dense
                  clearable
                  hide-details
                  type="number"
                  min="0"
                  label="Valor mínimo"
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="filters.maxValueInput"
                  outlined
                  dense
                  clearable
                  hide-details
                  type="number"
                  min="0"
                  label="Valor máximo"
                />
              </v-col>
            </v-row>
          </section>
        </v-card-text>

        <v-divider class="transactions-filters-dialog__divider" />

        <v-card-actions class="transactions-filters-dialog__footer">
          <v-btn text class="transactions-filters-dialog__footer-secondary" @click="filtersDialog = false">
            Cancelar
          </v-btn>
          <v-btn text color="#e5183d" @click="clearFiltersAndKeepModal">
            Limpar tudo
          </v-btn>
          <v-spacer />
          <v-btn
            color="#e5183d"
            dark
            depressed
            class="transactions-filters-dialog__apply"
            @click="applyFiltersFromModal"
          >
            <v-icon left small>mdi-check</v-icon>
            Aplicar filtros
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card outlined class="transactions-page__table-card">
      <v-data-table
        :header-props="{ sortByText: 'Ordenar por' }"
        :headers="headers"
        :items="transactions"
        :loading="loading"
        :server-items-length="totalTransactions"
        :footer-props="{ 'items-per-page-text': 'transações por página' }"
        :items-per-page.sync="itemsPerPage"
        :page.sync="page"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        no-data-text="Nenhuma transação encontrada"
        loading-text="Carregando transações..."
        class="elevation-0 transactions-page__data-table"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="transactions-page__cell transactions-page__cell--id">
                <span class="transactions-page__id-suffix" :title="item.id || ''">
                  {{ formatIdSuffixSix(item.id) }}
                </span>
              </td>
              <td class="transactions-page__cell transactions-page__cell--date">
                {{ formatDateCompact(item.createdAt || "") }}
              </td>
              <td class="transactions-page__cell transactions-page__cell--chip">
                <v-tooltip v-if="item.productCategory" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      v-on="on"
                      x-small
                      :color="productCategoryColor(item.productCategory)"
                      dark
                      class="transactions-page__chip transactions-page__chip--product"
                    >
                      {{ formatProductCategoryShort(item.productCategory) }}
                    </v-chip>
                  </template>
                  <span>{{ formatProductCategory(item.productCategory) }}</span>
                </v-tooltip>
                <span v-else class="transactions-page__empty">—</span>
              </td>
              <td class="transactions-page__cell transactions-page__cell--truncate">
                <span :title="getDisplayPartnerName(item)">
                  {{ getDisplayPartnerName(item) }}
                </span>
              </td>
              <td class="transactions-page__cell transactions-page__cell--truncate">
                <span :title="item.responsibleAssociate?.name || ''">
                  {{ item.responsibleAssociate?.name || "—" }}
                </span>
              </td>
              <td class="transactions-page__cell transactions-page__cell--customer">
                <span
                  class="transactions-page__customer-name"
                  :title="item.customer?.name || ''"
                >
                  {{ item.customer?.name || "—" }}
                </span>
                <span
                  v-if="item.customer?.document"
                  class="transactions-page__customer-doc"
                >
                  {{ formatDocument(item.customer.document) }}
                </span>
              </td>
              <td class="transactions-page__cell transactions-page__cell--num">
                {{ formatPrice(item.value) }}
              </td>
              <td class="transactions-page__cell transactions-page__cell--chip">
                <v-chip
                  x-small
                  :color="statusColor(item.status)"
                  dark
                  class="transactions-page__chip transactions-page__chip--status"
                >
                  {{ formatStatus(item.status) }}
                </v-chip>
              </td>
              <td class="transactions-page__cell transactions-page__cell--actions text-right">
                <v-btn
                  icon
                  x-small
                  color="#e5183d"
                  aria-label="Ver detalhes da transação"
                  @click="openTransactionDetail(item)"
                >
                  <v-icon small>mdi-text-box-search-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      max-width="640"
      scrollable
      content-class="transactions-detail-dialog__backdrop"
    >
      <v-card v-if="transactionDetail" class="transactions-detail-dialog">
        <div class="transactions-detail-dialog__accent" aria-hidden="true" />
        <v-card-title class="transactions-detail-dialog__header">
          <div>
            <div class="text-h6">Detalhes da transação</div>
            <div class="transactions-detail-dialog__subtitle text-body-2">
              ID completo: <span class="transactions-detail-dialog__mono">{{ transactionDetail.id }}</span>
            </div>
            <div
              v-if="detailTypeHint"
              class="transactions-detail-dialog__hint text-body-2"
            >
              {{ detailTypeHint }}
            </div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Fechar" @click="closeTransactionDetail">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="transactions-detail-dialog__body">
          <section class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">Identificação e datas</h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr v-if="detailHasTrimmedText(transactionDetail.externalId)">
                  <td class="transactions-detail-dialog__k">ID externo</td>
                  <td class="transactions-detail-dialog__v">
                    {{ transactionDetail.externalId }}
                  </td>
                </tr>
                <tr>
                  <td class="transactions-detail-dialog__k">Criado em</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatDate(transactionDetail.createdAt || "") }}
                  </td>
                </tr>
                <tr>
                  <td class="transactions-detail-dialog__k">Atualizado em</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatDate(transactionDetail.updatedAt || "") }}
                  </td>
                </tr>
                <tr>
                  <td class="transactions-detail-dialog__k">Status</td>
                  <td class="transactions-detail-dialog__v">
                    <v-chip small :color="statusColor(transactionDetail.status)" dark>
                      {{ formatStatus(transactionDetail.status) }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="transactions-detail-dialog__k">Tipo</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatTransactionType(transactionDetail.type) }}
                  </td>
                </tr>
                <tr v-if="transactionDetail.productCategory">
                  <td class="transactions-detail-dialog__k">Produto</td>
                  <td class="transactions-detail-dialog__v">
                    <v-chip
                      small
                      :color="productCategoryColor(transactionDetail.productCategory)"
                      dark
                    >
                      {{ formatProductCategory(transactionDetail.productCategory) }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="detailShouldShowPaymentRow">
                  <td class="transactions-detail-dialog__k">Pagamento</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPaymentMethod(transactionDetail.paymentMethod) }}
                  </td>
                </tr>
                <tr v-if="detailHasTrimmedText(transactionDetail.description)">
                  <td class="transactions-detail-dialog__k">Descrição</td>
                  <td class="transactions-detail-dialog__v">
                    {{ transactionDetail.description }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">Valores</h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr>
                  <td class="transactions-detail-dialog__k">Valor</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.value) }}
                  </td>
                </tr>
                <tr v-if="detailMonetaryPresent(transactionDetail.brpointsValue)">
                  <td class="transactions-detail-dialog__k">BRP</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.brpointsValue) }}
                  </td>
                </tr>
                <tr v-if="detailMonetaryPresent(transactionDetail.franchiseValue)">
                  <td class="transactions-detail-dialog__k">Valor franquia</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.franchiseValue) }}
                  </td>
                </tr>
                <tr v-if="detailMonetaryPresent(transactionDetail.partnerTax)">
                  <td class="transactions-detail-dialog__k">Taxa parceiro</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.partnerTax) }}
                  </td>
                </tr>
                <tr v-if="detailMonetaryPresent(transactionDetail.partnerValue)">
                  <td class="transactions-detail-dialog__k">Valor parceiro</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.partnerValue) }}
                  </td>
                </tr>
                <tr v-if="detailMonetaryPresent(transactionDetail.phasesValue)">
                  <td class="transactions-detail-dialog__k">Fases</td>
                  <td class="transactions-detail-dialog__v">
                    {{ formatPrice(transactionDetail.phasesValue) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-if="detailEnrolledPartnerRows.length" class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">Parceiro aderente</h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr v-for="(row, idx) in detailEnrolledPartnerRows" :key="'dep-' + idx">
                  <td class="transactions-detail-dialog__k">{{ row.label }}</td>
                  <td class="transactions-detail-dialog__v">{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-if="detailPartnerRows.length" class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">
              {{ transactionDetail.productCategory === 'PARTNER_ADHESION' ? 'Conta de recebimento' : 'Parceiro' }}
            </h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr v-for="(row, idx) in detailPartnerRows" :key="'dp-' + idx">
                  <td class="transactions-detail-dialog__k">{{ row.label }}</td>
                  <td class="transactions-detail-dialog__v">{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-if="detailAssociateRows.length" class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">
              {{ transactionDetail.productCategory === 'ASSOCIATE_PREMIUM' ? 'Associado assinante' : 'Associado responsável' }}
            </h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr v-for="(row, idx) in detailAssociateRows" :key="'da-' + idx">
                  <td class="transactions-detail-dialog__k">{{ row.label }}</td>
                  <td class="transactions-detail-dialog__v">{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-if="detailCustomerRows.length" class="transactions-detail-dialog__block">
            <h3 class="transactions-detail-dialog__label">Consumidor</h3>
            <v-simple-table dense class="transactions-detail-dialog__table elevation-0">
              <tbody>
                <tr v-for="(row, idx) in detailCustomerRows" :key="'dc-' + idx">
                  <td class="transactions-detail-dialog__k">{{ row.label }}</td>
                  <td class="transactions-detail-dialog__v">{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>
        </v-card-text>
        <v-divider />
        <v-card-actions class="transactions-detail-dialog__footer">
          <v-spacer />
          <v-btn text @click="closeTransactionDetail">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { format, parseISO } from "date-fns";

import { FranchiseType } from "@/types/franchise/Franchise";
import {
  PaginatedTransactionsResponse,
  TransactionFiltersRequest,
  TransactionProductCategory,
  Transactions,
} from "@/types/transaction/Transaction";

import { GetAllFranchises } from "@/services/franchises/GetAllFranchises";
import { GetTransaction } from "@/services/transactions/GetTransactions";
import {
  formatTransactionProductCategory,
  transactionProductCategoryColor,
} from "@/utils/transactionProductCategory";

type FilterChip = {
  key: string;
  label: string;
};

type QueryValue = string | (string | null)[] | null | undefined;

const DEFAULT_PERIOD_PRESET = "LAST_7_DAYS";
const DEFAULT_TRANSACTION_TYPE = "PAYMENT";
const DEFAULT_TRANSACTION_STATUS = "CONFIRMED";

/** Exibe linha de pagamento (cartão/Pix) — compra ou quando a API envia o campo */
const TRANSACTION_TYPES_WITH_PAYMENT_CONTEXT = new Set(["PAYMENT", "INSTANT_CASHBACK"]);

type DetailRow = { label: string; value: string };

const formatToIsoDate = (date: Date) => date.toISOString().split("T")[0];

const getPresetRange = (preset: string) => {
  const today = new Date();
  const endDate = formatToIsoDate(today);
  const startReference = new Date(today);

  if (preset === "TODAY") {
    return { startDate: endDate, endDate };
  }

  if (preset === "LAST_7_DAYS") {
    startReference.setDate(today.getDate() - 6);
    return {
      startDate: formatToIsoDate(startReference),
      endDate,
    };
  }

  startReference.setDate(today.getDate() - 29);
  return {
    startDate: formatToIsoDate(startReference),
    endDate,
  };
};

export default defineComponent({
  data() {
    const { startDate, endDate } = getPresetRange(DEFAULT_PERIOD_PRESET);

    return {
      headers: [
        { text: "ID", value: "id", sortable: false, width: "64px" },
        { text: "Data", value: "createdAt", width: "118px" },
        { text: "Produto", value: "productCategory", sortable: false, width: "88px" },
        { text: "Parceiro", value: "partnerName" },
        { text: "Associado", value: "responsibleAssociate", sortable: false },
        { text: "Consumidor", value: "customerName" },
        { text: "Valor", value: "value", align: "end", width: "96px" },
        { text: "Status", value: "status", width: "96px" },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "end",
          width: "44px",
        },
      ],
      detailDialog: false,
      transactionDetail: null as Transactions | null,
      statusOptions: [
        { text: "Pendente", value: "PENDING" },
        { text: "Concluída", value: "CONFIRMED" },
        { text: "Cancelada", value: "REFUSED" },
      ],
      paymentMethodOptions: [
        { text: "Pix", value: "PIX" },
        { text: "Cartão de crédito", value: "CREDIT_CARD" },
      ],
      typeOptions: [
        { text: "Pagamento", value: "PAYMENT" },
        { text: "Cashback", value: "CASHBACK" },
        { text: "Saque", value: "WITHDRAWAL" },
        { text: "Indicação", value: "INDICATION_BONUS" },
        { text: "Cashback instantâneo", value: "INSTANT_CASHBACK" },
      ],
      productCategoryOptions: [
        { text: "Adesão de parceiro", value: "PARTNER_ADHESION" },
        { text: "Assinatura premium de associado", value: "ASSOCIATE_PREMIUM" },
      ],
      periodPresetOptions: [
        { text: "Hoje", value: "TODAY" },
        { text: "Últimos 7 dias", value: "LAST_7_DAYS" },
        { text: "Últimos 30 dias", value: "LAST_30_DAYS" },
        { text: "Personalizado", value: "CUSTOM" },
      ],
      filters: {
        search: "",
        transactionId: "",
        status: DEFAULT_TRANSACTION_STATUS as string | undefined,
        type: DEFAULT_TRANSACTION_TYPE as string | undefined,
        productCategory: undefined as TransactionProductCategory | undefined,
        paymentMethod: undefined as string | undefined,
        ibge: undefined as string | undefined,
        partnerName: "",
        associateName: "",
        minValueInput: "",
        maxValueInput: "",
        startDate,
        endDate,
      },
      transactions: [] as Transactions[],
      franchises: [] as FranchiseType[],
      selectedRange: [startDate, endDate] as string[],
      filtersDialog: false,
      dateDialog: false,
      loading: false,
      periodPreset: DEFAULT_PERIOD_PRESET,
      page: 1,
      itemsPerPage: 10,
      sortBy: "createdAt",
      sortDesc: true,
      totalTransactions: 0,
      initialized: false,
      searchDebounceTimer: null as number | null,
    };
  },
  computed: {
    formattedDateRange(): string {
      if (!this.selectedRange.length) {
        return "";
      }

      const orderedRange = this.getOrderedRange(this.selectedRange);
      return orderedRange
        .map((date: string) => this.formatDateOnly(date))
        .join(" até ");
    },
    activeFilterChips(): FilterChip[] {
      const chips: FilterChip[] = [];

      chips.push({
        key: "period",
        label: `Período: ${this.periodPresetLabel(this.periodPreset)} (${this.formattedDateRange})`,
      });

      if (this.filters.search) {
        chips.push({ key: "search", label: `Busca: ${this.filters.search}` });
      }

      if (this.filters.transactionId) {
        chips.push({ key: "transactionId", label: `ID: ${this.filters.transactionId}` });
      }

      if (this.filters.status) {
        chips.push({ key: "status", label: `Status: ${this.formatStatus(this.filters.status)}` });
      }

      if (this.filters.type) {
        chips.push({
          key: "type",
          label: `Tipo: ${this.formatTransactionType(this.filters.type)}`,
        });
      }

      if (this.filters.productCategory) {
        chips.push({
          key: "productCategory",
          label: `Produto: ${this.formatProductCategory(this.filters.productCategory)}`,
        });
      }

      if (this.filters.paymentMethod) {
        chips.push({
          key: "paymentMethod",
          label: `Pagamento: ${this.formatPaymentMethod(this.filters.paymentMethod)}`,
        });
      }

      if (this.filters.ibge) {
        chips.push({
          key: "ibge",
          label: `Franquia: ${this.getFranchiseLabel(this.filters.ibge)}`,
        });
      }

      if (this.filters.partnerName) {
        chips.push({
          key: "partnerName",
          label: `Parceiro: ${this.filters.partnerName}`,
        });
      }

      if (this.filters.associateName) {
        chips.push({
          key: "associateName",
          label: `Associado: ${this.filters.associateName}`,
        });
      }

      if (this.filters.minValueInput) {
        chips.push({
          key: "minValue",
          label: `Valor mínimo: ${this.formatPrice(this.filters.minValueInput)}`,
        });
      }

      if (this.filters.maxValueInput) {
        chips.push({
          key: "maxValue",
          label: `Valor máximo: ${this.formatPrice(this.filters.maxValueInput)}`,
        });
      }

      return chips;
    },
    detailTypeHint(): string {
      const type = this.transactionDetail?.type;
      if (!type) {
        return "";
      }

      const hints: Record<string, string> = {
        PAYMENT:
          "Compra no parceiro: pagamento e repasses só aparecem quando a API enviar esses campos.",
        INSTANT_CASHBACK:
          "Cashback na compra: pagamento e demais campos seguem o que vier na resposta.",
        CASHBACK:
          "Crédito de cashback: parceiro, associado e repasses aparecem apenas se preenchidos.",
        WITHDRAWAL:
          "Saque: sem método de pagamento; consumidor e valores conforme retorno da API.",
        INDICATION_BONUS:
          "Bônus de indicação: apenas informações retornadas para este tipo.",
      };

      return hints[type] || "";
    },
    detailShouldShowPaymentRow(): boolean {
      const row = this.transactionDetail;
      if (!row) {
        return false;
      }

      if (TRANSACTION_TYPES_WITH_PAYMENT_CONTEXT.has(row.type)) {
        return true;
      }

      return !!row.paymentMethod;
    },
    detailPartnerRows(): DetailRow[] {
      const partner = this.transactionDetail?.partner;
      if (!partner) {
        return [];
      }

      const rows: DetailRow[] = [];
      const name = partner.name?.trim();
      if (name) {
        rows.push({ label: "Nome", value: name });
      }

      if (partner.document?.trim()) {
        rows.push({
          label: "Documento",
          value: this.formatDocument(partner.document),
        });
      }

      if (partner.email?.trim()) {
        rows.push({ label: "E-mail", value: partner.email.trim() });
      }

      const city = this.formatCity(partner.cityName, partner.state);
      if (city !== "—") {
        rows.push({ label: "Cidade / UF", value: city });
      }

      return rows;
    },
    detailEnrolledPartnerRows(): DetailRow[] {
      const partner = this.transactionDetail?.enrolledPartner;
      if (!partner) {
        return [];
      }

      const rows: DetailRow[] = [];
      if (partner.name?.trim()) {
        rows.push({ label: "Nome", value: partner.name.trim() });
      }

      if (partner.document?.trim()) {
        rows.push({
          label: "Documento",
          value: this.formatDocument(partner.document),
        });
      }

      if (partner.email?.trim()) {
        rows.push({ label: "E-mail", value: partner.email.trim() });
      }

      if (partner.phone?.trim()) {
        rows.push({ label: "Telefone", value: partner.phone.trim() });
      }

      return rows;
    },
    detailAssociateRows(): DetailRow[] {
      const associate =
        this.transactionDetail?.responsibleAssociate ||
        this.transactionDetail?.partner?.franchiseAssociate;
      if (!associate) {
        return [];
      }

      const rows: DetailRow[] = [];
      if (associate.name?.trim()) {
        rows.push({ label: "Nome", value: associate.name.trim() });
      }

      if (associate.document?.trim()) {
        rows.push({
          label: "Documento",
          value: this.formatDocument(associate.document),
        });
      }

      if (associate.email?.trim()) {
        rows.push({ label: "E-mail", value: associate.email.trim() });
      }

      if (associate.phone?.trim()) {
        rows.push({ label: "Telefone", value: associate.phone.trim() });
      }

      return rows;
    },
    detailCustomerRows(): DetailRow[] {
      const customer = this.transactionDetail?.customer;
      if (!customer) {
        return [];
      }

      const rows: DetailRow[] = [];
      if (customer.name?.trim()) {
        rows.push({ label: "Nome", value: customer.name.trim() });
      }

      if (customer.document?.trim()) {
        rows.push({
          label: "CPF / documento",
          value: this.formatDocument(customer.document),
        });
      }

      if (customer.email?.trim()) {
        rows.push({ label: "E-mail", value: customer.email.trim() });
      }

      if (customer.phone?.trim()) {
        rows.push({ label: "Telefone", value: customer.phone.trim() });
      }

      return rows;
    },
  },
  watch: {
    "filters.search"() {
      if (!this.initialized) {
        return;
      }

      this.scheduleDebouncedSearch();
    },
    page() {
      if (!this.initialized) {
        return;
      }

      this.fetchTransactions();
    },
    itemsPerPage() {
      if (!this.initialized) {
        return;
      }

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.fetchTransactions();
    },
    sortBy() {
      if (!this.initialized) {
        return;
      }

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.fetchTransactions();
    },
    sortDesc() {
      if (!this.initialized) {
        return;
      }

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.fetchTransactions();
    },
  },
  methods: {
    async handleLoadFranchises() {
      const getAllFranchises = new GetAllFranchises();
      this.franchises = await getAllFranchises.execute();
    },
    async fetchTransactions() {
      this.loading = true;

      try {
        this.applySelectedRange();

        const getTransaction = new GetTransaction();
        const response = (await getTransaction.execute(
          this.buildRequestFilters()
        )) as PaginatedTransactionsResponse | Transactions[];

        if (Array.isArray(response)) {
          this.transactions = response;
          this.totalTransactions = response.length;
        } else {
          this.transactions = response.data || [];
          this.totalTransactions = response.total || 0;
        }

        this.syncQueryParams();
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer);
      }

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.fetchTransactions();
    },
    clearFilters() {
      const { startDate, endDate } = getPresetRange(DEFAULT_PERIOD_PRESET);

      this.filters = {
        search: "",
        transactionId: "",
        status: DEFAULT_TRANSACTION_STATUS,
        type: DEFAULT_TRANSACTION_TYPE,
        productCategory: undefined,
        paymentMethod: undefined,
        ibge: undefined,
        partnerName: "",
        associateName: "",
        minValueInput: "",
        maxValueInput: "",
        startDate,
        endDate,
      };
      this.selectedRange = [startDate, endDate];
      this.periodPreset = DEFAULT_PERIOD_PRESET;
      this.sortBy = "createdAt";
      this.sortDesc = true;
      this.itemsPerPage = 10;

      if (this.page !== 1) {
        this.page = 1;
        return;
      }

      this.fetchTransactions();
    },
    clearFiltersAndKeepModal() {
      const shouldKeepModalOpen = this.filtersDialog;
      this.clearFilters();
      this.filtersDialog = shouldKeepModalOpen;
    },
    removeFilter(key: string) {
      if (key === "period") {
        const { startDate, endDate } = getPresetRange(DEFAULT_PERIOD_PRESET);
        this.selectedRange = [startDate, endDate];
        this.filters.startDate = startDate;
        this.filters.endDate = endDate;
        this.periodPreset = DEFAULT_PERIOD_PRESET;
      }

      if (key === "search") this.filters.search = "";
      if (key === "transactionId") this.filters.transactionId = "";
      if (key === "status") this.filters.status = DEFAULT_TRANSACTION_STATUS;
      if (key === "type") this.filters.type = DEFAULT_TRANSACTION_TYPE;
      if (key === "productCategory") this.filters.productCategory = undefined;
      if (key === "paymentMethod") this.filters.paymentMethod = undefined;
      if (key === "ibge") this.filters.ibge = undefined;
      if (key === "partnerName") this.filters.partnerName = "";
      if (key === "associateName") this.filters.associateName = "";
      if (key === "minValue") this.filters.minValueInput = "";
      if (key === "maxValue") this.filters.maxValueInput = "";

      this.applyFilters();
    },
    applyFiltersFromModal() {
      this.filtersDialog = false;
      this.applyFilters();
    },
    closeDateDialog() {
      this.dateDialog = false;
    },
    confirmDateRange() {
      const orderedRange = this.getOrderedRange(this.selectedRange);
      this.selectedRange = orderedRange;
      this.periodPreset = "CUSTOM";
      this.applySelectedRange();
      this.dateDialog = false;
    },
    handlePeriodPresetChange(preset: string) {
      if (preset === "CUSTOM") {
        this.dateDialog = true;
        return;
      }

      const { startDate, endDate } = getPresetRange(preset);
      this.selectedRange = [startDate, endDate];
      this.filters.startDate = startDate;
      this.filters.endDate = endDate;
    },
    scheduleDebouncedSearch() {
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = window.setTimeout(() => {
        if (this.page !== 1) {
          this.page = 1;
          return;
        }

        this.fetchTransactions();
      }, 450);
    },
    buildRequestFilters(): TransactionFiltersRequest {
      return {
        search: this.normalizeText(this.filters.search),
        transactionId: this.normalizeText(this.filters.transactionId),
        status: this.filters.status,
        type: this.filters.type,
        productCategory: this.filters.productCategory,
        paymentMethod: this.filters.paymentMethod,
        ibge: this.filters.ibge,
        partnerName: this.normalizeText(this.filters.partnerName),
        associateName: this.normalizeText(this.filters.associateName),
        minValue: this.parseNumber(this.filters.minValueInput),
        maxValue: this.parseNumber(this.filters.maxValueInput),
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        paginate: true,
        take: this.itemsPerPage,
        skip: (this.page - 1) * this.itemsPerPage,
        sortBy: this.sortBy,
        sortOrder: this.sortDesc ? "DESC" : "ASC",
      };
    },
    applySelectedRange() {
      const orderedRange = this.getOrderedRange(this.selectedRange);
      this.selectedRange = orderedRange;
      this.filters.startDate = orderedRange[0];
      this.filters.endDate = orderedRange[1];
    },
    getOrderedRange(range: string[]) {
      if (!range.length) {
        const { startDate, endDate } = getPresetRange(DEFAULT_PERIOD_PRESET);
        return [startDate, endDate];
      }

      const normalizedRange =
        range.length === 1 ? [range[0], range[0]] : [...range].slice(0, 2);

      return normalizedRange[0] > normalizedRange[1]
        ? [normalizedRange[1], normalizedRange[0]]
        : normalizedRange;
    },
    getPresetRange(preset: string) {
      return getPresetRange(preset);
    },
    formatToIsoDate(date: Date) {
      return formatToIsoDate(date);
    },
    parseNumber(value: string) {
      if (!value) {
        return undefined;
      }

      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? undefined : parsedValue;
    },
    normalizeText(value: string) {
      const normalizedValue = value?.trim();
      return normalizedValue ? normalizedValue : undefined;
    },
    syncQueryParams() {
      const query: Record<string, string> = {
        preset: this.periodPreset,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        page: String(this.page),
        itemsPerPage: String(this.itemsPerPage),
        sortBy: this.sortBy,
        sortDesc: String(this.sortDesc),
      };

      const optionalValues: Record<string, string | undefined> = {
        search: this.normalizeText(this.filters.search),
        transactionId: this.normalizeText(this.filters.transactionId),
        status: this.filters.status,
        type: this.filters.type,
        productCategory: this.filters.productCategory,
        paymentMethod: this.filters.paymentMethod,
        ibge: this.filters.ibge,
        partnerName: this.normalizeText(this.filters.partnerName),
        associateName: this.normalizeText(this.filters.associateName),
        minValue: this.filters.minValueInput || undefined,
        maxValue: this.filters.maxValueInput || undefined,
      };

      Object.entries(optionalValues).forEach(([key, value]) => {
        if (value) {
          query[key] = value;
        }
      });

      this.$router.replace({ query }).catch(() => undefined);
    },
    hydrateFromQuery() {
      const query = this.$route.query;

      const preset = this.getQueryValue(query.preset) || DEFAULT_PERIOD_PRESET;
      const fallbackRange = getPresetRange(
        preset === "CUSTOM" ? DEFAULT_PERIOD_PRESET : preset
      );

      const startDate = this.getQueryValue(query.startDate) || fallbackRange.startDate;
      const endDate = this.getQueryValue(query.endDate) || fallbackRange.endDate;

      this.periodPreset = preset;
      this.selectedRange = [startDate, endDate];
      this.filters.startDate = startDate;
      this.filters.endDate = endDate;
      this.filters.search = this.getQueryValue(query.search) || "";
      this.filters.transactionId = this.getQueryValue(query.transactionId) || "";
      this.filters.status =
        this.getQueryValue(query.status) || DEFAULT_TRANSACTION_STATUS;
      this.filters.type = this.getQueryValue(query.type) || DEFAULT_TRANSACTION_TYPE;
      this.filters.productCategory = this.parseProductCategory(
        this.getQueryValue(query.productCategory),
      );
      this.filters.paymentMethod = this.getQueryValue(query.paymentMethod) || undefined;
      this.filters.ibge = this.getQueryValue(query.ibge) || undefined;
      this.filters.partnerName = this.getQueryValue(query.partnerName) || "";
      this.filters.associateName = this.getQueryValue(query.associateName) || "";
      this.filters.minValueInput = this.getQueryValue(query.minValue) || "";
      this.filters.maxValueInput = this.getQueryValue(query.maxValue) || "";
      this.page = this.parsePositiveInt(this.getQueryValue(query.page), 1);
      this.itemsPerPage = this.parsePositiveInt(
        this.getQueryValue(query.itemsPerPage),
        10
      );
      this.sortBy = this.getQueryValue(query.sortBy) || "createdAt";
      this.sortDesc = this.getQueryValue(query.sortDesc) !== "false";
    },
    getQueryValue(value: QueryValue) {
      if (Array.isArray(value)) {
        return value[0] || undefined;
      }

      return value || undefined;
    },
    parsePositiveInt(value: string | undefined, fallback: number) {
      const parsedValue = Number(value);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
    },
    parseProductCategory(
      value: string | undefined,
    ): TransactionProductCategory | undefined {
      if (value === "PARTNER_ADHESION" || value === "ASSOCIATE_PREMIUM") {
        return value;
      }

      return undefined;
    },
    getFranchiseLabel(ibge: string) {
      const franchise = this.franchises.find((item) => item.ibge === ibge);
      return franchise
        ? `${franchise.cityName} - ${franchise.state}`
        : ibge;
    },
    periodPresetLabel(preset: string) {
      const option = this.periodPresetOptions.find((item) => item.value === preset);
      return option?.text || "Personalizado";
    },
    formatDate(date: string): string {
      const parsedDate = parseISO(date);
      parsedDate.setUTCHours(parsedDate.getUTCHours());
      return format(parsedDate, "dd/MM/yyyy HH:mm:ss");
    },
    formatDateCompact(date: string): string {
      if (!date) {
        return "—";
      }

      const parsedDate = parseISO(date);
      parsedDate.setUTCHours(parsedDate.getUTCHours());
      return format(parsedDate, "dd/MM/yy HH:mm");
    },
    formatDateOnly(date: string): string {
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    formatPrice(value: number | string): string {
      if (value === undefined || value === null || value === "") {
        return "";
      }

      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
        return String(value);
      }

      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }).format(parsedValue);
    },
    formatDocument(document: string): string {
      if (!document) {
        return "—";
      }

      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }

      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }

      return document;
    },
    formatPaymentMethod(paymentMethod: string | undefined): string {
      if (paymentMethod === "PIX") return "Pix";
      if (paymentMethod === "CREDIT_CARD") return "Cartão de crédito";
      return "Indefinido";
    },
    formatTransactionType(type: string): string {
      const labels: Record<string, string> = {
        WITHDRAWAL: "Saque",
        PAYMENT: "Pagamento",
        CASHBACK: "Cashback",
        INDICATION_BONUS: "Indicação",
        INSTANT_CASHBACK: "Cashback instantâneo",
      };

      return labels[type] || type;
    },
    formatProductCategory(category: string | undefined): string {
      return formatTransactionProductCategory(category as any);
    },
    formatProductCategoryShort(category: string | undefined): string {
      if (category === "PARTNER_ADHESION") {
        return "Adesão";
      }

      if (category === "ASSOCIATE_PREMIUM") {
        return "Premium";
      }

      return "—";
    },
    productCategoryColor(category: string | undefined): string {
      return transactionProductCategoryColor(category as any);
    },
    getDisplayPartnerName(item: Transactions): string {
      if (item.productCategory === "PARTNER_ADHESION") {
        return item.enrolledPartner?.name || "—";
      }

      return item.partner?.name || "—";
    },
    formatStatus(status: string): string {
      const labels: Record<string, string> = {
        PENDING: "Pendente",
        CONFIRMED: "Concluída",
        REFUSED: "Cancelada",
      };

      return labels[status] || status;
    },
    statusColor(status: string): string {
      if (status === "CONFIRMED") return "green darken-1";
      if (status === "PENDING") return "orange darken-2";
      if (status === "REFUSED") return "red darken-1";
      return "grey darken-1";
    },
    formatCity(cityName?: string | null, state?: string | null) {
      if (!cityName && !state) {
        return "—";
      }

      return [cityName, state].filter(Boolean).join(" - ");
    },
    formatIdSuffixSix(id: string | undefined) {
      if (!id) {
        return "—";
      }

      const lastHyphen = id.lastIndexOf("-");
      const afterHyphen = lastHyphen >= 0 ? id.slice(lastHyphen + 1) : id;
      if (!afterHyphen) {
        return "—";
      }

      return afterHyphen.length <= 6 ? afterHyphen : afterHyphen.slice(-6);
    },
    formatPriceOrDash(value: number | string | null | undefined) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }

      return this.formatPrice(value);
    },
    detailHasTrimmedText(value: string | null | undefined): boolean {
      if (value === null || value === undefined) {
        return false;
      }

      return String(value).trim().length > 0;
    },
    detailMonetaryPresent(value: number | string | null | undefined): boolean {
      if (value === null || value === undefined || value === "") {
        return false;
      }

      if (typeof value === "string" && value.trim() === "") {
        return false;
      }

      return true;
    },
    openTransactionDetail(row: Transactions) {
      this.transactionDetail = row;
      this.detailDialog = true;
    },
    closeTransactionDetail() {
      this.detailDialog = false;
      this.transactionDetail = null;
    },
  },
  async mounted() {
    await this.handleLoadFranchises();
    this.hydrateFromQuery();
    this.initialized = true;
    await this.fetchTransactions();
  },
});
</script>

<style scoped>
.transactions-page__filters {
  margin-bottom: 20px;
}

.transactions-page__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.transactions-page__toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.transactions-page__chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 16px;
}

.transactions-page__chips-label {
  font-size: 0.875rem;
  font-weight: 600;
  margin-right: 8px;
}

.transactions-page__results {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}

.transactions-page__data-table ::v-deep .v-data-table__wrapper {
  overflow-x: hidden;
}

.transactions-page__data-table ::v-deep table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}

.transactions-page__data-table ::v-deep thead th,
.transactions-page__data-table ::v-deep tbody td {
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.transactions-page__data-table ::v-deep thead th {
  white-space: nowrap;
  vertical-align: bottom;
  font-size: 0.75rem;
}

.transactions-page__cell {
  vertical-align: middle;
  line-height: 1.35;
  font-size: 0.8125rem;
}

.transactions-page__cell--date {
  white-space: nowrap;
  font-size: 0.75rem;
}

.transactions-page__cell--id {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.75rem;
}

.transactions-page__id-suffix {
  font-weight: 600;
  letter-spacing: 0.04em;
}

.transactions-page__cell--actions {
  white-space: nowrap;
  padding-right: 4px !important;
}

.transactions-page__cell--num {
  text-align: right;
  white-space: nowrap;
  font-weight: 600;
}

.transactions-page__cell--chip {
  vertical-align: middle;
}

.transactions-page__cell--truncate span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transactions-page__cell--customer {
  line-height: 1.25;
}

.transactions-page__customer-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.transactions-page__customer-doc {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.6875rem;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
}

.transactions-page__empty {
  color: rgba(0, 0, 0, 0.38);
}

.transactions-page__chip {
  max-width: 100%;
}

.transactions-page__chip--product {
  font-weight: 600;
}

.transactions-page__chip--status {
  width: 100%;
  justify-content: center;
}

.transactions-detail-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.transactions-detail-dialog__accent {
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, #e5183d, #ff6b6b);
}

.transactions-detail-dialog__header {
  padding: 16px 16px 12px !important;
  align-items: flex-start;
}

.transactions-detail-dialog__subtitle {
  color: rgba(0, 0, 0, 0.58);
  margin-top: 4px;
  word-break: break-all;
}

.transactions-detail-dialog__hint {
  color: rgba(0, 0, 0, 0.5);
  margin-top: 10px;
  line-height: 1.45;
  max-width: 520px;
}

.transactions-detail-dialog__mono {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.8125rem;
}

.transactions-detail-dialog__body {
  padding: 16px 20px 8px !important;
  background: #fafafa;
}

.transactions-detail-dialog__block {
  margin-bottom: 20px;
}

.transactions-detail-dialog__block:last-child {
  margin-bottom: 4px;
}

.transactions-detail-dialog__label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  margin: 0 0 8px;
}

.transactions-detail-dialog__table {
  background: #fff !important;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.transactions-detail-dialog__table ::v-deep tbody tr:hover {
  background: transparent !important;
}

.transactions-detail-dialog__k {
  width: 38%;
  max-width: 180px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.8125rem !important;
  vertical-align: top;
}

.transactions-detail-dialog__v {
  font-size: 0.875rem !important;
  word-break: break-word;
}

.transactions-detail-dialog__footer {
  background: #fff;
  padding: 8px 12px 12px !important;
}

/* Modal de filtros — layout e hierarquia visual */
.transactions-filters-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.transactions-filters-dialog__accent {
  height: 4px;
  width: 100%;
  background: linear-gradient(90deg, #e5183d 0%, #ff6b6b 50%, #e5183d 100%);
  background-size: 200% 100%;
}

.transactions-filters-dialog__header {
  align-items: flex-start;
  padding: 20px 20px 16px !important;
}

.transactions-filters-dialog__header-text {
  min-width: 0;
  padding-top: 2px;
}

.transactions-filters-dialog__title-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.transactions-filters-dialog__title-icon {
  flex-shrink: 0;
}

.transactions-filters-dialog__title {
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.transactions-filters-dialog__subtitle {
  color: rgba(0, 0, 0, 0.58);
  line-height: 1.4;
  margin-top: 2px;
  max-width: 420px;
}

.transactions-filters-dialog__divider {
  border-color: rgba(0, 0, 0, 0.06) !important;
}

.transactions-filters-dialog__body {
  padding: 20px 24px 8px !important;
  background: #fafafa;
}

.transactions-filters-dialog__section {
  margin-bottom: 22px;
  padding-bottom: 4px;
}

.transactions-filters-dialog__section--last {
  margin-bottom: 8px;
}

.transactions-filters-dialog__section-label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 6px;
}

.transactions-filters-dialog__section-hint {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.52);
  line-height: 1.45;
  margin: 0 0 12px;
  max-width: 640px;
}

.transactions-filters-dialog__body ::v-deep .v-input .v-input__slot {
  background: #fff !important;
}

.transactions-filters-dialog__footer {
  padding: 12px 16px 16px !important;
  background: #fff;
  flex-wrap: wrap;
  gap: 8px;
}

.transactions-filters-dialog__footer-secondary {
  color: rgba(0, 0, 0, 0.6) !important;
}

.transactions-filters-dialog__apply.v-btn {
  min-width: 148px !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(229, 24, 61, 0.28) !important;
}

.transactions-filters-dialog__date-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}
</style>

<style lang="scss">
/* content-class do v-dialog fica no wrapper do Vuetify (fora do scoped) */
.v-dialog__content.transactions-filters-dialog__backdrop {
  padding: 20px 16px;
}

.v-dialog__content.transactions-detail-dialog__backdrop {
  padding: 16px;
}
</style>
